import React, { useRef, useState } from 'react';

const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024;      // 5 MB (pre-compression ceiling)
const MAX_DIM = 1920;                    // downscale long edge to this
const ACCEPT = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

function fmtSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// Downscale + re-encode to JPEG via canvas. Returns a File.
function compress(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (Math.max(width, height) > MAX_DIM) {
        const s = MAX_DIM / Math.max(width, height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return resolve(file);
        const base = (file.name || 'screenshot').replace(/\.[^.]+$/, '');
        resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.82);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// Controlled: parent holds `items` = [{ id, file, name, origSize, newSize, previewUrl }]
export default function ScreenshotUploader({ items, setItems, disabled }) {
  const inputRef = useRef(null);
  const [err, setErr] = useState('');
  const [drag, setDrag] = useState(false);

  async function addFiles(fileList) {
    setErr('');
    const incoming = Array.from(fileList || []);
    let msg = '';
    for (const f of incoming) {
      if (items.length >= MAX_FILES) { msg = `Up to ${MAX_FILES} screenshots.`; break; }
      if (!ACCEPT.includes(f.type)) { msg = `${f.name}: images only (PNG, JPG, WebP).`; continue; }
      if (f.size > MAX_BYTES) { msg = `${f.name}: exceeds 5 MB.`; continue; }
      const compressed = await compress(f);
      setItems(prev => prev.length >= MAX_FILES ? prev : [...prev, {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file: compressed, name: f.name, origSize: f.size, newSize: compressed.size,
        previewUrl: URL.createObjectURL(compressed),
      }]);
    }
    if (msg) setErr(msg);
    if (inputRef.current) inputRef.current.value = '';
  }

  function remove(id) {
    setItems(prev => {
      const it = prev.find(x => x.id === id);
      if (it) URL.revokeObjectURL(it.previewUrl);
      return prev.filter(x => x.id !== id);
    });
  }

  const full = items.length >= MAX_FILES;

  return (
    <div>
      {!full && !disabled && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          style={{ border: `1.5px dashed ${drag ? '#0B6623' : 'var(--n-300)'}`, borderRadius: 10,
            padding: '16px', textAlign: 'center', cursor: 'pointer', background: drag ? 'var(--action-bg)' : 'var(--bg-app)' }}>
          <div style={{ fontSize: 22 }}>🖼️</div>
          <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 4 }}>
            Drag screenshots here, or <span style={{ color: '#0B6623', fontWeight: 700 }}>browse</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            PNG, JPG or WebP · up to {MAX_FILES} files · 5 MB each · auto-compressed
          </div>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple
        style={{ display: 'none' }} onChange={e => addFiles(e.target.files)} />

      {err && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>⚠ {err}</div>}

      {items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginTop: 10 }}>
          {items.map(it => (
            <div key={it.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
              <div style={{ height: 76, background: 'var(--n-100)' }}>
                <img src={it.previewUrl} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '5px 8px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</div>
                <div style={{ fontSize: 10, color: 'var(--status-resolved-text)', marginTop: 1 }}>✓ {fmtSize(it.origSize)} → {fmtSize(it.newSize)}</div>
              </div>
              {!disabled && (
                <button type="button" onClick={() => remove(it.id)} aria-label="Remove"
                  style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none',
                    background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>✕</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
